import { formatMoneyString } from "@/composables/formatUtils";

type Translate = (key: string, values?: Record<string, string>) => string;

/** The label for one price (a backend decimal string): "42.500 €/MWh", or "Not priced yet" while null. */
export const priceLabel = (t: Translate, price: string | null): string =>
  price === null
    ? t("forwards.common.not_priced")
    : t("forwards.common.price_per_mwh", { price: formatMoneyString(price) });

/**
 * The label for a row's price span, as the forwards tables show it: one "42.500 €/MWh", a range
 * "38.000 – 45.000 €/MWh", or "Not priced yet" while either end is null.
 *
 * `min` and `max` are the backend's decimal strings (`price_min` / `price_max`).
 */
export const priceRangeLabel = (
  t: Translate,
  min: string | null,
  max: string | null
): string => {
  if (min === null || max === null) return priceLabel(t, null);
  if (min === max) return priceLabel(t, min);
  return t("forwards.common.price_range_per_mwh", {
    min: formatMoneyString(min),
    max: formatMoneyString(max),
  });
};

/** `PlanDeliveryState.unit_price`'s bounds: `ge=0, max_digits=13, decimal_places=3`. */
const UNIT_PRICE_PATTERN = /^\d{1,10}(\.\d{1,3})?$/;

/**
 * Whether a typed price is one the plan save accepts as a delivery's price: zero or more, at most
 * 3 decimals and 13 digits. A touched number input hands back a number, so either is accepted.
 */
export const isValidUnitPrice = (value: string | number): boolean =>
  UNIT_PRICE_PATTERN.test(String(value).trim());
